import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Download, Edit, Trash2, Plus, Home, MessageCircle, Users, LogOut, Image, Video, Send, X, User, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Login from "./components/Login";
import CageDetails from "./components/CageDetails";
import SingleSubRoom from "./components/SingleSubRoom";
import ViewToggle from "./components/ViewToggle";
import "./components/ViewToggle.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function MainApp() {
  const { user, logout, getAuthHeaders } = useAuth();
  const [animals, setAnimals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAnimal, setEditingAnimal] = useState(null);
  const [mainTab, setMainTab] = useState("home");
  const [farmTab, setFarmTab] = useState("cages");
  const [homeTab, setHomeTab] = useState("overview");
  const [showMyProfile, setShowMyProfile] = useState(false);
  const [profileEmail, setProfileEmail] = useState("");
  const [profilePhone, setProfilePhone] = useState("");
  const [profileNextOfKin, setProfileNextOfKin] = useState("");
  const [profileNextOfKinContact, setProfileNextOfKinContact] = useState("");
  const [profilePhoto, setProfilePhoto] = useState("");
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [users, setUsers] = useState([]);
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newUserFullName, setNewUserFullName] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserPhone, setNewUserPhone] = useState("");
  const [newUserAddress, setNewUserAddress] = useState("");
  const [newUserNextOfKin, setNewUserNextOfKin] = useState("");
  const [newUserNextOfKinContact, setNewUserNextOfKinContact] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [currentPassword, setCurrentPassword] = useState('Loading...');
  const [systemSettings, setSystemSettings] = useState({
    system_name: 'DK Farms',
    logo_url: 'https://customer-assets.emergentagent.com/job_farmflow-system-2/artifacts/1e5nqcxy_LOGO%20new.jpg'
  });
  const [showActivityLogs, setShowActivityLogs] = useState(false);
  const [showEditUserProfile, setShowEditUserProfile] = useState(false);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [showPendingChanges, setShowPendingChanges] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserFullName, setEditUserFullName] = useState("");
  const [editUserEmail, setEditUserEmail] = useState("");
  const [editUserPhone, setEditUserPhone] = useState("");
  const [editUserAddress, setEditUserAddress] = useState("");
  const [editUserNextOfKin, setEditUserNextOfKin] = useState("");
  const [editUserNextOfKinContact, setEditUserNextOfKinContact] = useState("");
  const [editUserProfilePhoto, setEditUserProfilePhoto] = useState("");
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [showAdminPasswordReset, setShowAdminPasswordReset] = useState(false);
  const [currentPasswordInput, setCurrentPasswordInput] = useState("");
  const [newPasswordReset, setNewPasswordReset] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetTargetUsername, setResetTargetUsername] = useState("");
  const [subRooms, setSubRooms] = useState([]);
  const [allCages, setAllCages] = useState([]);
  const [selectedCage, setSelectedCage] = useState(null);
  const [selectedSubRoom, setSelectedSubRoom] = useState(null);
  const [subRoomDialogOpen, setSubRoomDialogOpen] = useState(false);
  const [singleSubRoomView, setSingleSubRoomView] = useState(false);
  const [selectedSingleRoom, setSelectedSingleRoom] = useState(null);
  const [showAddSubRoomDialog, setShowAddSubRoomDialog] = useState(false);
  const [newSubRoomLabel, setNewSubRoomLabel] = useState("");
  const [addSubRoomCage, setAddSubRoomCage] = useState("");
  const [animalPhotoInput, setAnimalPhotoInput] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const animalPhotoRef = useRef(null);
  const previousMessageCountRef = useRef(0);
  const audioContextRef = useRef(null);
  const previousTaskCountRef = useRef(0);
  
  // Task states
  const [tasks, setTasks] = useState([]);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDescription, setNewTaskDescription] = useState("");
  
  // Order states
  const [pendingOrders, setPendingOrders] = useState([]);
  const [completedOrders, setCompletedOrders] = useState([]);
  const previousOrderCountRef = useRef(0);
  
  // Bill states
  const [bills, setBills] = useState([]);
  
  // User management pending changes
  const [pendingUserChanges, setPendingUserChanges] = useState([]);
  
  // Collapsible section states for homepage organization
  const [sectionsExpanded, setSectionsExpanded] = useState({
    tasks: true,
    orders: true,
    completedOrders: true,
    pendingApprovals: true,
    pendingUserChanges: true,
    myProfile: false,
    farmStats: true,
    analytics: true
  });
  
  const toggleSection = (section) => {
    setSectionsExpanded(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  const [formData, setFormData] = useState({
    animal_type: "rabbit",
    breed: "",
    age: "",
    gender: "male",
    purchase_date: "",
    birth_date: "",
    weight: "",
    health_status: "healthy",
    cage_number: "",
    sub_room: "",
    photo: "",
    group: "babies"
  });

  useEffect(() => {
    if (user) {
      fetchData();
      fetchBills();
      fetchPendingUserChanges();
    }
  }, [user]);

  // Ensure home tab is shown by default for all users
  useEffect(() => {
    if (user && !mainTab) {
      setMainTab("home");
    }
  }, [user, mainTab]);

  // Live chat polling - aggressive refresh for instant communication
  useEffect(() => {
    let interval;
    
    if (user) {
      // Initial fetch
      fetchMessages();
      
      // Ultra-fast polling: 1 second for live feel
      interval = setInterval(() => {
        fetchMessages();
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [user]);

  // Immediate refresh when switching to chat tab
  useEffect(() => {
    if (mainTab === "chat" && user) {
      fetchMessages();
    }
  }, [mainTab]);

  // Task polling - check for new tasks every 3 seconds
  useEffect(() => {
    let interval;
    
    if (user) {
      // Initial fetch
      fetchTasks();
      
      // Poll every 3 seconds
      interval = setInterval(() => {
        fetchTasks();
      }, 3000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [user]);

  // Pending orders polling - all users, check every 5 seconds
  useEffect(() => {
    let interval;
    
    if (user) {
      // Initial fetch
      fetchPendingOrders();
      
      // Poll every 5 seconds
      interval = setInterval(() => {
        fetchPendingOrders();
      }, 5000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [user]);

  useEffect(() => {
    if (mainTab === "users" && user?.is_admin) {
      fetchUsers();
    }
  }, [mainTab, user]);

  // Auto-refresh data when returning to farm tab
  useEffect(() => {
    if (mainTab === "farm" && user) {
      fetchData();
    }
  }, [mainTab]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchBills = async () => {
    try {
      const response = await axios.get(`${API}/bills`, { headers: getAuthHeaders() });
      setBills(response.data);
    } catch (error) {
      console.error("Error fetching bills:", error);
    }
  };

  const fetchData = async () => {
    try {
      
      // Fetch system settings for all users
      fetchSystemSettings();
      
      const [animalsRes, statsRes, cagesRes] = await Promise.all([
        axios.get(`${API}/animals`, { headers: getAuthHeaders() }),
        axios.get(`${API}/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/sub-rooms`, { headers: getAuthHeaders() })
      ]);
      setAnimals(animalsRes.data);
      setStats(statsRes.data);
      setSubRooms(cagesRes.data); // Store all sub-rooms
      
      // Extract unique cage numbers from sub-rooms
      const uniqueCages = [...new Set(cagesRes.data.map(room => room.cage_number))].sort();
      setAllCages(uniqueCages);
      
      // Fetch pending changes if user is admin
      if (user?.is_admin) {
        try {
          const pendingRes = await axios.get(`${API}/pending-changes`, { headers: getAuthHeaders() });
          setPendingChanges(pendingRes.data);
          
          // Fetch current password for admin
          fetchCurrentPassword();
        } catch (err) {
          // Silent fail for pending changes
          console.log("Could not fetch pending changes");
        }
      }
      
      setLoading(false);
      
      // Show success toast only if user triggered refresh manually
      if (window.dataRefreshTriggered) {
        toast.success("Data refreshed from cages");
        window.dataRefreshTriggered = false;
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        logout();
      } else {
        toast.error("Failed to load data");
      }
      setLoading(false);
    }
  };
  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/chat/messages`, { headers: getAuthHeaders() });
      const newMessages = response.data;
      
      // Only update if messages have changed (optimization)
      setMessages(prevMessages => {
        // Compare message counts and last message ID
        if (prevMessages.length !== newMessages.length || 
            (newMessages.length > 0 && prevMessages.length > 0 && 
             newMessages[newMessages.length - 1].id !== prevMessages[prevMessages.length - 1].id)) {
          
          // Play notification sound for ALL new messages (not on initial load)
          if (previousMessageCountRef.current > 0 && newMessages.length > previousMessageCountRef.current) {
            const latestMessage = newMessages[newMessages.length - 1];
            
            // Notify for ALL messages (incoming and outgoing)
            playNotificationSound();
            
            // Show toast notification with message details
            if (latestMessage.username === user?.username) {
              toast.success(`You: ${latestMessage.message || 'Sent a media file'}`, {
                duration: 2000,
                position: 'top-right'
              });
            } else {
              toast.info(`${latestMessage.username}: ${latestMessage.message || 'Sent a media file'}`, {
                duration: 3000,
                position: 'top-right'
              });
            }
          }
          
          // Update the previous message count
          previousMessageCountRef.current = newMessages.length;
          
          return newMessages;
        }
        return prevMessages;
      });
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, { headers: getAuthHeaders() });
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users");
    }
  };

  const fetchCurrentPassword = async () => {
    if (user?.is_admin) {
      try {
        const response = await axios.get(`${API}/users/${user?.id}/password`, { headers: getAuthHeaders() });
        setCurrentPassword(response.data.password);
      } catch (error) {
        setCurrentPassword('[Unable to retrieve]');
      }
    }
  };

  const fetchSystemSettings = async () => {
    try {
      const response = await axios.get(`${API}/system/settings`, { headers: getAuthHeaders() });
      setSystemSettings(response.data);
    } catch (error) {
      console.error("Error fetching system settings:", error);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/tasks`, { headers: getAuthHeaders() });
      const newTasks = response.data;
      
      setTasks(prevTasks => {
        // Check if new tasks were added
        if (prevTasks.length !== newTasks.length ||
            (newTasks.length > 0 && prevTasks.length > 0 &&
             newTasks[newTasks.length - 1].id !== prevTasks[prevTasks.length - 1].id)) {
          
          // Play notification sound if new tasks arrived (not on initial load)
          if (previousTaskCountRef.current > 0 && newTasks.length > previousTaskCountRef.current) {
            playNotificationSound();
          }
          
          previousTaskCountRef.current = newTasks.length;
          return newTasks;
        }
        return prevTasks;
      });
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
  };

  const fetchPendingUserChanges = async () => {
    if (user && user.is_admin) {
      try {
        const response = await axios.get(`${API}/pending-changes`, { headers: getAuthHeaders() });
        setPendingUserChanges(response.data);
      } catch (error) {
        console.error("Error fetching pending user changes:", error);
      }
    }
  };

  const fetchPendingOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, { headers: getAuthHeaders() });
      const allOrders = response.data;
      const pending = allOrders.filter(order => order.status === 'pending');
      const completed = allOrders.filter(order => order.status === 'completed' || order.status === 'paid');
      
      setPendingOrders(prevOrders => {
        // Check if new orders arrived
        if (prevOrders.length !== pending.length ||
            (pending.length > 0 && prevOrders.length > 0 &&
             pending[pending.length - 1].id !== prevOrders[prevOrders.length - 1].id)) {
          
          // Play notification sound if new orders arrived (not on initial load)
          if (previousOrderCountRef.current > 0 && pending.length > previousOrderCountRef.current) {
            playNotificationSound();
            toast.info(`🔔 New order received! ${pending.length} pending order${pending.length > 1 ? 's' : ''} awaiting approval`, {
              duration: 5000,
              style: {
                background: '#f97316',
                color: '#fff',
              },
            });
          }
          
          previousOrderCountRef.current = pending.length;
          return pending;
        }
        return prevOrders;
      });
      
      setCompletedOrders(completed);
    } catch (error) {
      console.error("Error fetching orders:", error);
    }
  };

  const handleApproveOrder = async (orderId) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, { status: 'approved' }, { headers: getAuthHeaders() });
      toast.success("Order approved successfully");
      fetchPendingOrders();
    } catch (error) {
      console.error("Error approving order:", error);
      toast.error("Failed to approve order");
    }
  };

  const handleCompleteOrder = async (orderId) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, { status: 'completed' }, { headers: getAuthHeaders() });
      toast.success("Order marked as completed");
      fetchPendingOrders();
    } catch (error) {
      console.error("Error completing order:", error);
      toast.error("Failed to complete order");
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm("Are you sure you want to delete this order?")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/orders/${orderId}`, { headers: getAuthHeaders() });
      toast.success("Order deleted successfully");
      fetchPendingOrders();
    } catch (error) {
      console.error("Error deleting order:", error);
      toast.error("Failed to delete order");
    }
  };

  const handleCreateTask = async () => {
    if (!newTaskTitle.trim()) {
      toast.error("Task title is required");
      return;
    }
    
    try {
      await axios.post(`${API}/tasks`, {
        title: newTaskTitle,
        description: newTaskDescription
      }, { headers: getAuthHeaders() });
      
      toast.success("Task created successfully");
      setNewTaskTitle("");
      setNewTaskDescription("");
      setShowTaskDialog(false);
      fetchTasks();
    } catch (error) {
      console.error("Error creating task:", error);
      toast.error("Failed to create task");
    }
  };

  const handleToggleTaskComplete = async (taskId, isCompleted) => {
    try {
      console.log('Toggling task:', taskId, 'isCompleted:', isCompleted);
      const endpoint = isCompleted ? 'uncomplete' : 'complete';
      await axios.post(`${API}/tasks/${taskId}/${endpoint}`, {}, { headers: getAuthHeaders() });
      await fetchTasks();
      toast.success(isCompleted ? "Task unmarked" : "Task completed!");
    } catch (error) {
      console.error("Error toggling task:", error);
      toast.error("Failed to update task");
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Are you sure you want to delete this task?")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/tasks/${taskId}`, { headers: getAuthHeaders() });
      toast.success("Task deleted successfully");
      fetchTasks();
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error("Failed to delete task");
    }
  };

  const fetchSubRoomsByGroup = async (group) => {
    try {
      const response = await axios.get(`${API}/animals/group/${group}`, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      console.error(`Error fetching ${group} animals:`, error);
      return [];
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAnimal) {
        await axios.put(`${API}/animals/${editingAnimal.id}`, formData, { headers: getAuthHeaders() });
        toast.success("Animal updated successfully");
      } else {
        await axios.post(`${API}/animals`, formData, { headers: getAuthHeaders() });
        toast.success("Animal added successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error saving animal:", error);
      toast.error("Failed to save animal");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this animal?")) {
      try {
        const response = await axios.delete(`${API}/animals/${id}`, { headers: getAuthHeaders() });
        // Check if response indicates pending approval (only for delete)
        if (response.data.status === "pending_approval") {
          toast.info(response.data.message || "Delete request submitted for admin approval");
        } else {
          toast.success("Animal deleted successfully");
        }
        fetchData();
      } catch (error) {
        console.error("Error deleting animal:", error);
        toast.error("Failed to delete animal");
      }
    }
  };

  const handleEdit = (animal) => {
    setEditingAnimal(animal);
    setFormData({
      animal_type: animal.animal_type,
      breed: animal.breed,
      age: animal.age,
      gender: animal.gender || "male",
      purchase_date: animal.purchase_date || "",
      birth_date: animal.birth_date || "",
      weight: animal.weight,
      health_status: animal.health_status,
      cage_number: animal.cage_number,
      sub_room: animal.sub_room || "",
      photo: animal.photo || "",
      group: animal.group,
      pregnancy_start_date: animal.pregnancy_start_date || ""
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingAnimal(null);
    setFormData({
      animal_type: "rabbit",
      breed: "",
      age: "",
      gender: "male",
      purchase_date: "",
      birth_date: "",
      weight: "",
      health_status: "healthy",
      cage_number: "",
      sub_room: "",
      photo: "",
      group: "babies",
      pregnancy_start_date: ""
    });
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (newPasswordReset !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    try {
      await axios.put(`${API}/profile/password`, {
        current_password: currentPasswordInput,
        new_password: newPasswordReset
      }, { headers: getAuthHeaders() });
      toast.success("Password updated successfully");
      setShowPasswordReset(false);
      setCurrentPasswordInput("");
      setNewPasswordReset("");
      setConfirmPassword("");
      
      // Refresh to get new password display
      fetchCurrentPassword();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    }
  };

  const handleAdminPasswordReset = async (e) => {
    e.preventDefault();
    if (!resetTargetUsername || !newPasswordReset) {
      toast.error("Please fill in all fields");
      return;
    }
    if (newPasswordReset.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await axios.post(`${API}/auth/admin-reset-password`, {
        username: resetTargetUsername,
        new_password: newPasswordReset
      }, { headers: getAuthHeaders() });
      toast.success(`Password reset successfully for ${resetTargetUsername}`);
      setShowAdminPasswordReset(false);
      setResetTargetUsername("");
      setNewPasswordReset("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    }
  };

  const fetchSubRooms = async (cageNumber) => {
    try {
      const response = await axios.get(`${API}/sub-rooms?cage_number=${cageNumber}`, { headers: getAuthHeaders() });
      setSubRooms(response.data);
    } catch (error) {
      console.error("Error fetching sub-rooms:", error);
    }
  };

  const handleInitializeSubRooms = async (cageNumber) => {
    try {
      await axios.post(`${API}/sub-rooms/initialize/${cageNumber}`, {}, { headers: getAuthHeaders() });
      toast.success(`Initialized sub-rooms for Cage ${cageNumber}`);
      fetchSubRooms(cageNumber);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initialize sub-rooms");
    }
  };

  const handleUpdateSubRoom = async (cageNumber, roomLabel, data) => {
    try {
      await axios.put(`${API}/sub-rooms/${cageNumber}/${roomLabel}`, data, { headers: getAuthHeaders() });
      toast.success("Sub-room updated successfully");
      fetchSubRooms(cageNumber);
    } catch (error) {
      toast.error("Failed to update sub-room");
    }
  };

  const handleAnimalPhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData({...formData, photo: reader.result});
    };
    reader.readAsDataURL(file);
  };

  const getUniqueCages = () => {
    // Return all cages from state, including empty ones
    return allCages;
  };

  const generateNextCageNumber = () => {
    const existingCages = getUniqueCages();
    if (existingCages.length === 0) {
      return "C-01";
    }
    
    // Extract numeric parts from cage numbers (e.g., "C-01" -> 1, "C-02" -> 2)
    const numbers = existingCages
      .map(cage => {
        const match = cage.match(/\d+/);
        return match ? parseInt(match[0]) : 0;
      })
      .filter(num => num > 0);
    
    const maxNumber = numbers.length > 0 ? Math.max(...numbers) : 0;
    const nextNumber = maxNumber + 1;
    
    // Format with leading zero (C-01, C-02, etc.)
    return `C-${String(nextNumber).padStart(2, '0')}`;
  };

  const handleAddCage = async () => {
    const nextCageNumber = generateNextCageNumber();
    
    // Automatically create cage without dialog
    try {
      await axios.post(`${API}/sub-rooms/initialize/${nextCageNumber}`, {}, { headers: getAuthHeaders() });
      toast.success(`Cage ${nextCageNumber} created successfully with 12 sub-rooms`);
      fetchData();
    } catch (error) {
      toast.error("Failed to create cage");
      console.error("Error creating cage:", error);
    }
  };

  const handleAddSubRoom = async () => {
    if (!newSubRoomLabel.trim()) {
      toast.error("Sub-room label is required");
      return;
    }

    if (!addSubRoomCage) {
      toast.error("Please select a cage");
      return;
    }

    try {
      await axios.post(`${API}/sub-rooms`, {
        cage_number: addSubRoomCage,
        room_label: newSubRoomLabel.toUpperCase(),
        capacity: 10
      }, { headers: getAuthHeaders() });
      
      toast.success(`Sub-room ${newSubRoomLabel.toUpperCase()} added to ${addSubRoomCage}`);
      setShowAddSubRoomDialog(false);
      setNewSubRoomLabel("");
      setAddSubRoomCage("");
      fetchData();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("Sub-room already exists");
      } else {
        toast.error("Failed to add sub-room");
      }
      console.error("Error adding sub-room:", error);
    }
  };

  const handleDeleteCage = async (cageNumber) => {
    const cageAnimals = animals.filter(a => a.cage_number === cageNumber);
    
    if (cageAnimals.length > 0) {
      toast.error(`Cannot delete cage ${cageNumber}. It has ${cageAnimals.length} animal(s). Please move or remove animals first.`);
      return;
    }

    if (window.confirm(`Are you sure you want to delete Cage ${cageNumber}?`)) {
      try {
        // Get all sub-rooms for this cage
        const response = await axios.get(`${API}/sub-rooms?cage_number=${cageNumber}`, { headers: getAuthHeaders() });
        const cageSubRooms = response.data;
        
        // Delete all sub-rooms
        for (const room of cageSubRooms) {
          try {
            await axios.delete(`${API}/sub-rooms/${cageNumber}/${room.room_label}`, { headers: getAuthHeaders() });
          } catch (error) {
            console.log(`Sub-room ${room.room_label} not found or already deleted`);
          }
        }
        toast.success(`Cage ${cageNumber} deleted successfully`);
        fetchData();
      } catch (error) {
        toast.error("Failed to delete cage");
        console.error("Error deleting cage:", error);
      }
    }
  };

  const handleSubRoomClick = (cageNumber, roomLabel) => {
    setSelectedSingleRoom({ cageNumber, roomLabel });
    setSingleSubRoomView(true);
  };

  const handleExport = async () => {
    try {
      // Determine what to export based on current view
      let viewType = 'all';
      let viewData = null;
      
      if (mainTab === 'farm') {
        viewType = farmTab; // dashboard, babies, mothers, crossed, pregnant, ready_to_sell, cages
        
        // If on a specific animal group tab, get those animals
        if (['babies', 'mothers', 'crossed', 'pregnant', 'ready_to_sell'].includes(farmTab)) {
          viewData = getGroupAnimals(farmTab);
        }
      } else if (mainTab === 'home') {
        viewType = 'dashboard';
      }
      
      const response = await axios.get(`${API}/export`, {
        params: { view: viewType },
        responseType: 'blob',
        headers: getAuthHeaders()
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename based on view
      const viewName = viewType.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('_');
      const filename = `DK_Farms_${viewName}_${new Date().toISOString().split('T')[0]}.pdf`;
      link.setAttribute('download', filename);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF exported successfully");
    } catch (error) {
      console.error("Error exporting data:", error);
      toast.error("Failed to export data");
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && !mediaPreview) return;

    try {
      await axios.post(`${API}/chat/messages`, {
        message: newMessage,
        media_type: mediaType,
        media_data: mediaPreview
      }, { headers: getAuthHeaders() });
      
      const messageContent = newMessage.trim();
      setNewMessage("");
      setMediaPreview(null);
      setMediaType(null);
      
      // Immediately fetch messages to see our sent message
      await fetchMessages();
      
      // Show success notification for sent message
      toast.success('Message sent successfully', {
        duration: 2000
      });
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
    }
  };

  const handleFileSelect = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setMediaPreview(reader.result);
      setMediaType(type);
    };
    reader.readAsDataURL(file);
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/register`, {
        username: newUsername,
        password: newPassword,
        full_name: newUserFullName,
        email: newUserEmail,
        phone: newUserPhone,
        address: newUserAddress,
        next_of_kin_name: newUserNextOfKin,
        next_of_kin_contact: newUserNextOfKinContact,
        is_admin: isAdmin
      }, { headers: getAuthHeaders() });
      toast.success("User added successfully");
      setNewUsername("");
      setNewPassword("");
      setNewUserFullName("");
      setNewUserEmail("");
      setNewUserPhone("");
      setNewUserAddress("");
      setNewUserNextOfKin("");
      setNewUserNextOfKinContact("");
      setIsAdmin(false);
      fetchUsers();
    } catch (error) {
      console.error("Error adding user:", error);
      toast.error(error.response?.data?.detail || "Failed to add user");
    }
  };

  const fetchActivityLogs = async () => {
    try {
      const response = await axios.get(`${API}/activity-logs?limit=100`, { headers: getAuthHeaders() });
      setActivityLogs(response.data);
    } catch (error) {
      console.error("Error fetching activity logs:", error);
      toast.error("Failed to load activity logs");
    }
  };

  const fetchPendingChanges = async () => {
    try {
      const response = await axios.get(`${API}/pending-changes`, { headers: getAuthHeaders() });
      setPendingChanges(response.data);
    } catch (error) {
      console.error("Error fetching pending changes:", error);
      toast.error("Failed to load pending changes");
    }
  };

  const handleApprovePendingChange = async (changeId) => {
    try {
      await axios.post(`${API}/pending-changes/${changeId}/approve`, {}, { headers: getAuthHeaders() });
      toast.success("Change approved successfully");
      fetchPendingChanges();
      fetchData();
    } catch (error) {
      toast.error("Failed to approve change");
    }
  };

  const handleRejectPendingChange = async (changeId) => {
    try {
      await axios.post(`${API}/pending-changes/${changeId}/reject`, {}, { headers: getAuthHeaders() });
      toast.success("Change rejected");
      fetchPendingChanges();
    } catch (error) {
      toast.error("Failed to reject change");
    }
  };

  const loadMyProfile = () => {
    setProfileEmail(user?.email || "");
    setProfilePhone(user?.phone || "");
    setProfileNextOfKin(user?.next_of_kin_name || "");
    setProfileNextOfKinContact(user?.next_of_kin_contact || "");
    setProfilePhoto(user?.profile_photo || "");
    setShowMyProfile(true);
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/users/${user.id}/profile`, {
        email: profileEmail,
        phone: profilePhone,
        next_of_kin_name: profileNextOfKin,
        next_of_kin_contact: profileNextOfKinContact,
        profile_photo: profilePhoto
      }, { headers: getAuthHeaders() });
      toast.success("Profile updated successfully");
      setShowMyProfile(false);
      // Refresh user data
      const response = await axios.get(`${API}/auth/me`, { headers: getAuthHeaders() });
      // Update user in context would be better, but for now we'll close dialog
    } catch (error) {
      toast.error("Failed to update profile");
    }
  };

  const handleProfilePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePhoto(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const openEditUserProfile = (userToEdit) => {
    setEditingUser(userToEdit);
    setEditUserFullName(userToEdit.full_name || "");
    setEditUserEmail(userToEdit.email || "");
    setEditUserPhone(userToEdit.phone || "");
    setEditUserAddress(userToEdit.address || "");
    setEditUserNextOfKin(userToEdit.next_of_kin_name || "");
    setEditUserNextOfKinContact(userToEdit.next_of_kin_contact || "");
    setEditUserProfilePhoto(userToEdit.profile_photo || "");
    setShowEditUserProfile(true);
  };

  const handleAdminUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/users/${editingUser.id}/admin-profile`, {
        full_name: editUserFullName,
        email: editUserEmail,
        phone: editUserPhone,
        address: editUserAddress,
        next_of_kin_name: editUserNextOfKin,
        next_of_kin_contact: editUserNextOfKinContact,
        profile_photo: editUserProfilePhoto
      }, { headers: getAuthHeaders() });
      toast.success("User profile updated successfully");
      setShowEditUserProfile(false);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update user profile");
    }
  };

  const handleEditUserPhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setEditUserProfilePhoto(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await axios.delete(`${API}/users/${userId}`, { headers: getAuthHeaders() });
        toast.success("User deleted successfully");
        fetchUsers();
      } catch (error) {
        console.error("Error deleting user:", error);
        toast.error(error.response?.data?.detail || "Failed to delete user");
      }
    }
  };

  const getGroupAnimals = (group) => {
    return animals.filter(a => a.group === group);
  };

  const getCageGroups = () => {
    const cageMap = {};
    animals.forEach(animal => {
      if (!cageMap[animal.cage_number]) {
        cageMap[animal.cage_number] = [];
      }
      cageMap[animal.cage_number].push(animal);
    });
    return cageMap;
  };

  const getHealthBadgeColor = (status) => {
    switch(status) {
      case "healthy": return "bg-green-500";
      case "sick": return "bg-red-500";
      case "under_treatment": return "bg-yellow-500";
      default: return "bg-gray-500";
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  // Play notification sound for new messages
  const playNotificationSound = () => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const context = audioContextRef.current;
      const oscillator = context.createOscillator();
      const gainNode = context.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(context.destination);
      
      oscillator.frequency.value = 800; // Frequency in Hz
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, context.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, context.currentTime + 0.3);
      
      oscillator.start(context.currentTime);
      oscillator.stop(context.currentTime + 0.3);
    } catch (error) {
      console.log('Could not play notification sound:', error);
    }
  };

  const AnimalTable = ({ animals }) => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Cage/Room</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Breed</TableHead>
          <TableHead>Age (months)</TableHead>
          <TableHead>Gender</TableHead>
          <TableHead>Weight (kg)</TableHead>
          <TableHead>Health</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {animals.length === 0 ? (
          <TableRow>
            <TableCell colSpan={8} className="text-center text-gray-500 py-8">
              No animals in this group yet. Add animals from the Cages tab.
            </TableCell>
          </TableRow>
        ) : (
          animals.map(animal => (
            <TableRow key={animal.id}>
              <TableCell>
                <div className="text-sm">
                  <div className="font-semibold">{animal.cage_number || 'N/A'}</div>
                  <div className="text-xs text-gray-500">Room {animal.sub_room || 'N/A'}</div>
                </div>
              </TableCell>
              <TableCell className="capitalize">{animal.animal_type.replace('_', ' ')}</TableCell>
              <TableCell>
                <div>
                  <div className="font-medium">{animal.breed}</div>
                  {animal.photo && (
                    <img src={animal.photo} alt={animal.breed} className="w-12 h-12 object-cover rounded mt-1" />
                  )}
                </div>
              </TableCell>
              <TableCell>{animal.age}</TableCell>
              <TableCell className="capitalize">{animal.gender || 'N/A'}</TableCell>
              <TableCell>{animal.weight}</TableCell>
              <TableCell>
                <Badge className={getHealthBadgeColor(animal.health_status)}>
                  {animal.health_status}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleEdit(animal)}
                    data-testid={`edit-animal-${animal.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    onClick={() => handleDelete(animal.id)}
                    data-testid={`delete-animal-${animal.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  {animal.cage_number && animal.sub_room && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedSingleRoom({ cageNumber: animal.cage_number, roomLabel: animal.sub_room });
                        setSingleSubRoomView(true);
                        setFarmTab("cages");
                      }}
                      title="Go to room"
                    >
                      <Home className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="app-container">
      <Toaster position="top-right" />
      
      <div className="header">
        <div className="header-content">
          <div className="flex items-center gap-3">
            <img 
              src={systemSettings.logo_url} 
              alt={`${systemSettings.system_name} Logo`} 
              className="w-16 h-16 object-contain rounded-lg"
            />
            <h1 className="header-title">{systemSettings.system_name}</h1>
          </div>
          <div className="flex gap-3 items-center">
            <span className="text-sm text-gray-600">
              Welcome, <strong>{user?.username}</strong>
              {user?.is_admin && <Badge className="ml-2 bg-blue-500">Admin</Badge>}
            </span>
            <Button 
              onClick={loadMyProfile}
              variant="outline"
              size="sm"
            >
              <User className="w-4 h-4 mr-2" />
              My Profile
            </Button>
            <Button 
              onClick={logout}
              variant="outline"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* View Toggle Button */}
      <ViewToggle />

      <div className="main-content">
        <Tabs value={mainTab} onValueChange={setMainTab}>
          <TabsList className="tabs-list mb-6">
            <TabsTrigger value="home" data-testid="main-tab-home">
              <Home className="w-4 h-4 mr-2" />
              Home
            </TabsTrigger>
            <TabsTrigger value="farm" data-testid="main-tab-farm">
              Farm Management
            </TabsTrigger>
            <TabsTrigger value="chat" data-testid="main-tab-chat">
              <MessageCircle className="w-4 h-4 mr-2" />
              Chat Room
            </TabsTrigger>
            {user?.is_admin && (
              <TabsTrigger value="users" data-testid="main-tab-users">
                <Users className="w-4 h-4 mr-2" />
                User Controls
              </TabsTrigger>
            )}
          </TabsList>

          {/* HOME TAB */}
          <TabsContent value="home">
            <div className="space-y-6">
              {/* Welcome Section with Logo - Visible to all users */}
              <Card className="border-2 border-blue-200 shadow-lg bg-gradient-to-r from-blue-50 to-white">
                <CardContent className="pt-6 pb-6">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-6">
                      <img 
                        src={systemSettings.logo_url} 
                        alt={`${systemSettings.system_name} Logo`} 
                        className="w-24 h-24 md:w-32 md:h-32 object-contain rounded-lg shadow-md"
                      />
                      <div>
                        <h2 className="text-3xl font-bold text-gray-800">Welcome to {systemSettings.system_name}</h2>
                        <p className="text-lg text-gray-600 mt-2">
                          Hello, <strong className="text-blue-600">{user?.username}</strong>
                          {user?.is_admin && <Badge className="ml-2 bg-blue-500">Admin</Badge>}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          Manage your farm operations efficiently
                        </p>
                      </div>
                    </div>
                    <div className="text-center md:text-right">
                      <p className="text-sm text-gray-500">Today's Date</p>
                      <p className="text-xl font-semibold text-gray-800">
                        {new Date().toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Task Board - Visible to all users - Collapsible */}
              <Card className="border-2 border-green-200 shadow-lg">
                <CardHeader 
                  className="bg-gradient-to-r from-green-50 to-white cursor-pointer hover:bg-green-100 transition-colors"
                  onClick={() => toggleSection('tasks')}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3 flex-1">
                      <div>
                        <CardTitle className="text-2xl text-green-800 flex items-center gap-2">
                          📋 Tasks & Announcements
                          <Badge className="bg-green-600">{tasks.length}</Badge>
                        </CardTitle>
                        <CardDescription>Important tasks and updates from admin</CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {user?.is_admin && (
                        <Button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowTaskDialog(true);
                          }} 
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          New Task
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" className="ml-2">
                        {sectionsExpanded.tasks ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                {sectionsExpanded.tasks && (
                  <CardContent className="pt-6">
                  {tasks.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No tasks yet. {user?.is_admin && "Create one to get started!"}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {tasks.map(task => {
                        const currentUserCompleted = task.completed_by?.includes(user?.username);
                        const anyUserCompleted = (task.completed_by?.length || 0) > 0;  // Show checked if ANY user completed
                        const completionCount = task.completed_by?.length || 0;
                        
                        return (
                          <Card key={task.id} className={`${anyUserCompleted ? 'bg-green-50 border-green-300' : 'bg-white border-gray-200'} border-2 transition-all hover:shadow-md`}>
                            <CardContent className="p-4">
                              <div className="flex items-start gap-3">
                                <input
                                  type="checkbox"
                                  checked={anyUserCompleted}
                                  onChange={() => handleToggleTaskComplete(task.id, currentUserCompleted)}
                                  className="mt-1 h-5 w-5 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
                                />
                                <div className="flex-1">
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <h3 className={`text-lg font-semibold ${anyUserCompleted ? 'text-green-700 line-through' : 'text-gray-800'}`}>
                                        {task.title}
                                      </h3>
                                      {task.description && (
                                        <p className={`mt-1 text-sm ${anyUserCompleted ? 'text-green-600' : 'text-gray-600'}`}>
                                          {task.description}
                                        </p>
                                      )}
                                      <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                                        <span>By: {task.created_by}</span>
                                        <span>•</span>
                                        <span>{new Date(task.created_at).toLocaleDateString()}</span>
                                        {user?.is_admin && (
                                          <>
                                            <span>•</span>
                                            <span className="text-green-600 font-semibold">
                                              {completionCount} {completionCount === 1 ? 'user' : 'users'} completed
                                            </span>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                    {user?.is_admin && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDeleteTask(task.id)}
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                  </CardContent>
                )}
              </Card>

              {/* Pending User Approvals - Admin Only */}
              {user?.is_admin && pendingUserChanges.length > 0 && (
                <Card className="border-2 border-red-200 shadow-lg">
                  <CardHeader 
                    className="bg-gradient-to-r from-red-50 to-white cursor-pointer hover:bg-red-100 transition-colors"
                    onClick={() => toggleSection('pendingUserChanges')}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <CardTitle className="text-2xl text-red-800 flex items-center gap-2">
                          ⚠️ Pending User Actions
                          <Badge className="bg-red-600">{pendingUserChanges.length}</Badge>
                        </CardTitle>
                        <CardDescription>User deletion requests awaiting admin approval</CardDescription>
                      </div>
                      <Button variant="ghost" size="sm" className="ml-2">
                        {sectionsExpanded.pendingUserChanges ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </Button>
                    </div>
                  </CardHeader>
                  {sectionsExpanded.pendingUserChanges && (
                    <CardContent className="pt-6">
                      <div className="space-y-3">
                        {pendingUserChanges.map(change => (
                          <Card key={change.id} className="border-2 border-red-100 bg-white hover:shadow-md transition-all">
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                      <span className="text-red-600 font-bold text-lg">
                                        {change.username.charAt(0).toUpperCase()}
                                      </span>
                                    </div>
                                    <div>
                                      <h3 className="font-semibold text-gray-800">{change.username}</h3>
                                      <p className="text-sm text-red-600 font-semibold">Deletion Request</p>
                                    </div>
                                  </div>
                                  <div className="mt-2 text-sm text-gray-600 ml-13">
                                    <p>
                                      <span className="font-semibold">Requested by:</span> {change.requested_by}
                                    </p>
                                    <p>
                                      <span className="font-semibold">Entity Type:</span> {change.entity_type}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                      {new Date(change.requested_at).toLocaleDateString()} at {new Date(change.requested_at).toLocaleTimeString()}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex flex-col gap-2">
                                  <Button
                                    size="sm"
                                    onClick={async () => {
                                      try {
                                        await axios.post(`${API}/pending-changes/${change.id}/approve`, {}, { headers: getAuthHeaders() });
                                        toast.success('Deletion approved');
                                        fetchPendingUserChanges();
                                        fetchData();
                                      } catch (error) {
                                        toast.error('Failed to approve');
                                      }
                                    }}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    ✓ Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={async () => {
                                      try {
                                        await axios.post(`${API}/pending-changes/${change.id}/reject`, {}, { headers: getAuthHeaders() });
                                        toast.success('Deletion rejected');
                                        fetchPendingUserChanges();
                                      } catch (error) {
                                        toast.error('Failed to reject');
                                      }
                                    }}
                                  >
                                    ✗ Reject
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </CardContent>
                  )}
                </Card>
              )}

              {/* Pending Approvals (Orders) - All Users - Collapsible */}
              {pendingOrders.length > 0 && (
                <Card className="border-2 border-orange-200 shadow-lg">
                  <CardHeader 
                    className="bg-gradient-to-r from-orange-50 to-white cursor-pointer hover:bg-orange-100 transition-colors"
                    onClick={() => toggleSection('pendingApprovals')}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <CardTitle className="text-2xl text-orange-800 flex items-center gap-2">
                          🛒 Pending Approvals
                          <Badge className="bg-orange-600">{pendingOrders.length}</Badge>
                        </CardTitle>
                        <CardDescription>Customer orders awaiting approval - All users can approve</CardDescription>
                      </div>
                      <Button variant="ghost" size="sm" className="ml-2">
                        {sectionsExpanded.pendingApprovals ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </Button>
                    </div>
                  </CardHeader>
                  {sectionsExpanded.pendingApprovals && (
                    <CardContent className="pt-6">
                    <div className="space-y-3">
                      {pendingOrders.slice(0, 5).map(order => (
                        <Card key={order.id} className="border-2 border-orange-100 bg-white hover:shadow-md transition-all">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                                    <span className="text-orange-600 font-bold text-lg">
                                      {order.customer_name.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                  <div>
                                    <h3 className="font-semibold text-gray-800">{order.customer_name}</h3>
                                    <p className="text-sm text-gray-600">{order.customer_phone}</p>
                                  </div>
                                </div>
                                <div className="mt-2 text-sm text-gray-600 ml-13">
                                  <p>
                                    <span className="font-semibold">Animal:</span> {order.animal_breed} ({order.animal_type})
                                  </p>
                                  <p>
                                    <span className="font-semibold">Quantity:</span> {order.quantity} • 
                                    <span className="font-semibold ml-2">Total:</span> ₵{order.total_price?.toFixed(2)}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {new Date(order.order_date).toLocaleDateString()} at {new Date(order.order_date).toLocaleTimeString()}
                                  </p>
                                </div>
                              </div>
                              <div className="flex flex-col gap-2">
                                <Button
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleApproveOrder(order.id);
                                  }}
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  ✓ Approve
                                </Button>
                                <Badge className="bg-orange-500 text-white text-center">
                                  PENDING
                                </Badge>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                      {pendingOrders.length > 5 && (
                        <p className="text-center text-sm text-gray-500 pt-2">
                          + {pendingOrders.length - 5} more pending orders
                        </p>
                      )}
                    </div>
                    </CardContent>
                  )}
                </Card>
              )}

              {/* Farm Statistics - Collapsible */}
              <Card className="border-2 border-blue-200 shadow-lg">
                <CardHeader 
                  className="bg-gradient-to-r from-blue-50 to-white cursor-pointer hover:bg-blue-100 transition-colors"
                  onClick={() => toggleSection('farmStats')}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle className="text-3xl text-blue-800">🏠 Farm Statistics</CardTitle>
                      <CardDescription>Overview of your farm population and management</CardDescription>
                    </div>
                    <Button variant="ghost" size="sm">
                      {sectionsExpanded.farmStats ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </Button>
                  </div>
                </CardHeader>
                {sectionsExpanded.farmStats && (
                  <CardContent className="pt-6">
                  <div className="stats-grid">
                    <Card className="stat-card">
                      <CardHeader>
                        <CardTitle>Rabbits Population</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="population-circle" data-testid="rabbits-population">
                          <div className="population-number">{stats?.rabbits_count || 0}</div>
                          <div className="population-label">Rabbits</div>
                        </div>
                        <div className="population-breakdown">
                          <div className="breakdown-item">
                            <span className="breakdown-label">Gender Breakdown:</span>
                          </div>
                          <div className="breakdown-sub-item">
                            <span className="breakdown-sublabel">Males: {stats?.rabbits_by_gender?.male || 0}</span>
                            <span className="breakdown-sublabel">Females: {stats?.rabbits_by_gender?.female || 0}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="stat-card">
                      <CardHeader>
                        <CardTitle>Grass Cutters Population</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="population-circle" data-testid="grass-cutters-population">
                          <div className="population-number">{stats?.grass_cutters_count || 0}</div>
                          <div className="population-label">Grass Cutters</div>
                        </div>
                        <div className="population-breakdown">
                          <div className="breakdown-item">
                            <span className="breakdown-label">Gender Breakdown:</span>
                          </div>
                          <div className="breakdown-sub-item">
                            <span className="breakdown-sublabel">Males: {stats?.grass_cutters_by_gender?.male || 0}</span>
                            <span className="breakdown-sublabel">Females: {stats?.grass_cutters_by_gender?.female || 0}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="stat-card">
                      <CardHeader>
                        <CardTitle>Total Cages</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="cage-display">
                          <div className="cage-number">{stats?.total_cages || 0}</div>
                          <div className="cage-label">Active Cages</div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="groups-grid mt-6">
                    <Card>
                      <CardHeader>
                        <CardTitle>Babies</CardTitle>
                        <CardDescription>Young animals</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="group-count">{stats?.groups?.babies || 0}</div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Mothers</CardTitle>
                        <CardDescription>Breeding females</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="group-count">{stats?.groups?.mothers || 0}</div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Crossed</CardTitle>
                        <CardDescription>Bred animals</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="group-count">{stats?.groups?.crossed || 0}</div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Pregnant</CardTitle>
                        <CardDescription>Expecting mothers</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="group-count">{stats?.groups?.pregnant || 0}</div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Weaning</CardTitle>
                        <CardDescription>Being weaned</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="group-count">{stats?.groups?.weaning || 0}</div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Sold</CardTitle>
                        <CardDescription>Sold animals</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="group-count">{stats?.groups?.sold || 0}</div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Ready to Sell</CardTitle>
                        <CardDescription>Market ready</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="group-count">{stats?.groups?.ready_to_sell || 0}</div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Quick Actions within Farm Stats */}
                  <div className="mt-6 pt-6 border-t border-blue-200">
                    <h3 className="text-xl font-semibold mb-4 text-blue-800">Quick Actions</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Button 
                        onClick={() => {
                          resetForm();
                          setDialogOpen(true);
                        }}
                        className="h-24 flex flex-col gap-2"
                      >
                        <Plus className="w-6 h-6" />
                        Add Animal
                      </Button>
                      <Button 
                        onClick={() => setMainTab("farm")}
                        variant="outline"
                        className="h-24 flex flex-col gap-2"
                      >
                        <Home className="w-6 h-6" />
                        Manage Farm
                      </Button>
                      <Button 
                        onClick={() => setMainTab("chat")}
                        variant="outline"
                        className="h-24 flex flex-col gap-2"
                      >
                        <MessageCircle className="w-6 h-6" />
                        Open Chat
                      </Button>
                      <Button 
                        onClick={handleExport}
                        variant="outline"
                        className="h-24 flex flex-col gap-2"
                      >
                        <Download className="w-6 h-6" />
                        Export Data
                      </Button>
                    </div>
                  </div>
                </CardContent>
                )}
              </Card>

              {/* My Profile Section - Admin Only */}
              {user?.is_admin && (
              <Card className="border-2 border-blue-200 shadow-lg">
                <CardHeader 
                  className="bg-gradient-to-r from-blue-50 to-white cursor-pointer hover:bg-blue-100 transition-colors"
                  onClick={() => toggleSection('myProfile')}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex-1">
                      <CardTitle className="text-2xl text-blue-800">👤 My Profile Settings</CardTitle>
                      <CardDescription>Update your personal information and password</CardDescription>
                    </div>
                    <Button variant="ghost" size="sm" className="ml-2">
                      {sectionsExpanded.myProfile ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </Button>
                  </div>
                </CardHeader>
                {sectionsExpanded.myProfile && (
                  <CardContent className="pt-6">
                    <div className="space-y-6">
                      {/* Current Password Display */}
                      <Card className="border border-gray-200 bg-gray-50">
                        <CardHeader>
                          <CardTitle className="text-lg">🔑 Current Credentials</CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label className="text-gray-600">Username</Label>
                              <p className="text-lg font-semibold text-gray-800">{user?.username}</p>
                            </div>
                            <div>
                              <Label className="text-gray-600">Current Password</Label>
                              <p className="text-lg font-mono font-semibold text-blue-600">{currentPassword}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Update Username and Personal Details */}
                      <Card className="border border-blue-100">
                        <CardHeader>
                          <CardTitle className="text-lg">Personal Information</CardTitle>
                        </CardHeader>
                      <CardContent className="pt-6">
                        <form onSubmit={async (e) => {
                          e.preventDefault();
                          const formData = new FormData(e.target);
                          try {
                            const updateData = {
                              phone: formData.get('phone'),
                              email: formData.get('email')
                            };
                            
                            const newUsername = formData.get('username');
                            if (newUsername && newUsername !== user?.username) {
                              updateData.username = newUsername;
                            }
                            
                            await axios.put(`${API}/profile`, updateData, { headers: getAuthHeaders() });
                            toast.success('Profile updated successfully');
                            fetchData();
                            
                            // If username changed, need to re-login
                            if (updateData.username) {
                              toast.info('Username changed - please login again');
                              setTimeout(() => logout(), 2000);
                            }
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to update profile');
                          }
                        }}>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="username">Username</Label>
                              <Input id="username" name="username" defaultValue={user?.username} placeholder="Enter new username (optional)" />
                              <p className="text-xs text-gray-500 mt-1">Leave unchanged to keep current username</p>
                            </div>
                            <div>
                              <Label htmlFor="phone">Phone Number</Label>
                              <Input id="phone" name="phone" defaultValue={user?.phone} placeholder="Enter phone number" />
                            </div>
                            <div>
                              <Label htmlFor="email">Email Address</Label>
                              <Input id="email" name="email" type="email" defaultValue={user?.email} placeholder="Enter email address" />
                            </div>
                          </div>
                          <div className="mt-6 flex justify-end">
                            <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                              Update Profile
                            </Button>
                          </div>
                        </form>
                      </CardContent>
                      </Card>

                      {/* System Settings - Admin Only */}
                      <Card className="border border-green-100">
                        <CardHeader>
                          <CardTitle className="text-lg">⚙️ System Settings</CardTitle>
                          <CardDescription>Customize system name and logo (Admin only)</CardDescription>
                        </CardHeader>
                        <CardContent className="pt-6">
                          <form onSubmit={async (e) => {
                            e.preventDefault();
                            const formData = new FormData(e.target);
                            try {
                              const logoFile = formData.get('logo_file');
                              let logoUrl = systemSettings.logo_url;
                              
                              // If a file is selected, upload it first
                              if (logoFile && logoFile.size > 0) {
                                // Convert file to base64
                                const reader = new FileReader();
                                reader.onloadend = async () => {
                                  const base64Image = reader.result;
                                  
                                  // Upload logo
                                  try {
                                    const uploadRes = await axios.post(`${API}/system/upload-logo`, {
                                      image_data: base64Image
                                    }, { headers: getAuthHeaders() });
                                    
                                    logoUrl = uploadRes.data.logo_url;
                                    
                                    // Now update settings with new logo
                                    await axios.put(`${API}/system/settings`, {
                                      system_name: formData.get('system_name'),
                                      logo_url: logoUrl
                                    }, { headers: getAuthHeaders() });
                                    
                                    toast.success('System settings and logo updated successfully');
                                    fetchSystemSettings();
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || 'Failed to upload logo');
                                  }
                                };
                                reader.readAsDataURL(logoFile);
                              } else {
                                // No file, just update settings
                                await axios.put(`${API}/system/settings`, {
                                  system_name: formData.get('system_name'),
                                  logo_url: systemSettings.logo_url
                                }, { headers: getAuthHeaders() });
                                toast.success('System name updated successfully');
                                fetchSystemSettings();
                              }
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Failed to update settings');
                            }
                          }}>
                            <div className="space-y-4">
                              <div>
                                <Label htmlFor="system_name">System Name</Label>
                                <Input 
                                  id="system_name" 
                                  name="system_name" 
                                  defaultValue={systemSettings.system_name} 
                                  placeholder="Enter system name (e.g., DK Farms)" 
                                  required
                                />
                                <p className="text-xs text-gray-500 mt-1">This name will appear in the header and welcome section</p>
                              </div>
                              
                              <div>
                                <Label htmlFor="logo_file">Upload New Logo</Label>
                                <Input 
                                  id="logo_file" 
                                  name="logo_file" 
                                  type="file"
                                  accept="image/*"
                                  className="cursor-pointer"
                                />
                                <p className="text-xs text-gray-500 mt-1">Select an image file (JPG, PNG, etc.) - Max 5MB</p>
                              </div>
                              
                              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-md border-2 border-dashed border-gray-300">
                                <img 
                                  src={systemSettings.logo_url} 
                                  alt="Current Logo Preview" 
                                  className="w-20 h-20 object-contain rounded border-2 border-gray-400 bg-white"
                                />
                                <div>
                                  <p className="text-sm font-semibold text-gray-700">Current Logo</p>
                                  <p className="text-xs text-gray-500">Upload a new file to replace this logo</p>
                                  <p className="text-xs text-blue-600 mt-1">
                                    💡 Recommended: Square image (500x500px or larger)
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="mt-6 flex justify-end">
                              <Button type="submit" className="bg-green-600 hover:bg-green-700">
                                💾 Save System Settings
                              </Button>
                            </div>
                          </form>
                        </CardContent>
                      </Card>

                      {/* Reset Password */}
                      <Card className="border border-purple-100">
                        <CardHeader>
                          <CardTitle className="text-lg">🔒 Change Password</CardTitle>
                        </CardHeader>
                      <CardContent className="pt-6">
                        <form onSubmit={async (e) => {
                          e.preventDefault();
                          const formData = new FormData(e.target);
                          const currentPassword = formData.get('current_password');
                          const newPassword = formData.get('new_password');
                          const confirmPassword = formData.get('confirm_password');

                          if (newPassword !== confirmPassword) {
                            toast.error('New passwords do not match');
                            return;
                          }

                          if (newPassword.length < 6) {
                            toast.error('Password must be at least 6 characters');
                            return;
                          }

                          try {
                            await axios.put(`${API}/profile/password`, {
                              current_password: currentPassword,
                              new_password: newPassword
                            }, { headers: getAuthHeaders() });
                            toast.success('Password updated successfully');
                            e.target.reset();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to update password');
                          }
                        }}>
                          <div className="space-y-4">
                            <div>
                              <Label htmlFor="current_password">Current Password *</Label>
                              <Input id="current_password" name="current_password" type="password" required placeholder="Enter current password" />
                            </div>
                            <div>
                              <Label htmlFor="new_password">New Password *</Label>
                              <Input id="new_password" name="new_password" type="password" required placeholder="Enter new password (min 6 characters)" />
                            </div>
                            <div>
                              <Label htmlFor="confirm_password">Confirm New Password *</Label>
                              <Input id="confirm_password" name="confirm_password" type="password" required placeholder="Confirm new password" />
                            </div>
                          </div>
                          <div className="mt-6 flex justify-end">
                            <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                              Update Password
                            </Button>
                          </div>
                        </form>
                      </CardContent>
                      </Card>
                    </div>
                  </CardContent>
                )}
              </Card>
              )}
            </div>
          </TabsContent>

          {/* FARM MANAGEMENT TAB */}
          <TabsContent value="farm">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">Farm Management</h2>
              <div className="flex gap-3">
                <Button 
                  onClick={handleExport} 
                  variant="outline"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
                <Button 
                  onClick={() => {
                    resetForm();
                    setDialogOpen(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Animal
                </Button>
              </div>
            </div>

            <Tabs value={farmTab} onValueChange={setFarmTab}>
              <TabsList className="tabs-list">
                <TabsTrigger value="cages">Cages</TabsTrigger>
                <TabsTrigger value="babies">Babies</TabsTrigger>
                <TabsTrigger value="mothers">Mothers</TabsTrigger>
                <TabsTrigger value="crossed">Crossed</TabsTrigger>
                <TabsTrigger value="pregnant">Pregnant</TabsTrigger>
                <TabsTrigger value="weaning">Weaning</TabsTrigger>
                <TabsTrigger value="sold">Sold</TabsTrigger>
                <TabsTrigger value="ready_to_sell">Ready to Sell</TabsTrigger>
              </TabsList>

              <TabsContent value="cages">
                {singleSubRoomView && selectedSingleRoom ? (
                  <SingleSubRoom
                    cageNumber={selectedSingleRoom.cageNumber}
                    roomLabel={selectedSingleRoom.roomLabel}
                    animals={animals}
                    getAuthHeaders={getAuthHeaders}
                    onClose={() => setSingleSubRoomView(false)}
                    onRefresh={fetchData}
                  />
                ) : (
                  <>
                    <div className="mb-4 flex justify-between items-center">
                      <p className="text-sm text-gray-600">Manage your cages and sub-rooms. Click on a sub-room to view its details.</p>
                      <div className="flex gap-2">
                        <Button onClick={handleAddCage} variant="outline">
                          <Plus className="w-4 h-4 mr-2" />
                          Add Cage
                        </Button>
                        <Button 
                          onClick={() => setShowAddSubRoomDialog(true)} 
                          variant="outline"
                          className="bg-blue-50 border-blue-300 hover:bg-blue-100"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Sub-Room
                        </Button>
                      </div>
                    </div>
                    <div className="cages-grid">
                      {getUniqueCages().length === 0 ? (
                        <Card className="col-span-full">
                          <CardContent className="text-center py-12">
                            <p className="text-gray-500 mb-4">No cages yet. Create your first cage to get started.</p>
                            <Button onClick={handleAddCage}>
                              <Plus className="w-4 h-4 mr-2" />
                              Create First Cage
                            </Button>
                          </CardContent>
                        </Card>
                      ) : (
                        getUniqueCages().map(cageNumber => {
                          const cageAnimals = animals.filter(a => a.cage_number === cageNumber);
                          // Get actual sub-rooms for this cage from backend data
                          const cageSubRooms = subRooms
                            .filter(room => room.cage_number === cageNumber)
                            .sort((a, b) => a.room_label.localeCompare(b.room_label));
                          
                          return (
                            <Card key={cageNumber} className="cage-card-detailed">
                              <CardHeader>
                                <div className="flex justify-between items-center">
                                  <div>
                                    <CardTitle>{cageNumber}</CardTitle>
                                    <CardDescription>{cageAnimals.length} animals total</CardDescription>
                                  </div>
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      onClick={() => {
                                        setSelectedCage(cageNumber);
                                        setSubRoomDialogOpen(true);
                                      }}
                                    >
                                      View All Rooms
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      onClick={() => handleDeleteCage(cageNumber)}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              </CardHeader>
                              <CardContent>
                                <div className="sub-rooms-compact">
                                  <div className="sub-rooms-header">
                                    Sub-Rooms ({cageSubRooms.length}) - Click to open:
                                  </div>
                                  <div className="sub-rooms-list">
                                    {cageSubRooms.map(room => {
                                      const roomLabel = room.room_label;
                                      const roomAnimals = cageAnimals.filter(a => a.sub_room === roomLabel);
                                      const hasAnimals = roomAnimals.length > 0;
                                      return (
                                        <div
                                          key={roomLabel}
                                          className={`sub-room-tag ${hasAnimals ? 'occupied' : 'empty'}`}
                                          onClick={() => handleSubRoomClick(cageNumber, roomLabel)}
                                          title={hasAnimals ? `${roomAnimals.length} animal(s) in Room ${roomLabel}` : `Room ${roomLabel} - Empty`}
                                        >
                                          <span className="room-label">{roomLabel}</span>
                                          {hasAnimals && <span className="room-count">{roomAnimals.length}</span>}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                                {cageAnimals.length > 0 && (
                                  <div className="cage-summary mt-3 pt-3 border-t">
                                    <div className="text-xs text-gray-600">
                                      <strong>Animals by Type:</strong> 
                                      {' '}Rabbits: {cageAnimals.filter(a => a.animal_type === 'rabbit').length}
                                      {' | '}Grass Cutters: {cageAnimals.filter(a => a.animal_type === 'grass_cutter').length}
                                    </div>
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                          );
                        })
                      )}
                    </div>
                  </>
                )}
              </TabsContent>

              <TabsContent value="babies">
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle>Babies ({getGroupAnimals("babies").length})</CardTitle>
                        <CardDescription>Young animals across all cages • Real-time data from cage rooms</CardDescription>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => { window.dataRefreshTriggered = true; fetchData(); }}
                      >
                        Refresh
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <AnimalTable animals={getGroupAnimals("babies")} />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="mothers">
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle>Mothers ({getGroupAnimals("mothers").length})</CardTitle>
                        <CardDescription>Breeding females across all cages • Real-time data from cage rooms</CardDescription>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => { window.dataRefreshTriggered = true; fetchData(); }}
                      >
                        Refresh
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <AnimalTable animals={getGroupAnimals("mothers")} />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="crossed">
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle>Crossed ({getGroupAnimals("crossed").length})</CardTitle>
                        <CardDescription>Bred animals across all cages • Real-time data from cage rooms</CardDescription>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => { window.dataRefreshTriggered = true; fetchData(); }}
                      >
                        Refresh
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <AnimalTable animals={getGroupAnimals("crossed")} />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="pregnant">
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle>Pregnant ({getGroupAnimals("pregnant").length})</CardTitle>
                        <CardDescription>Expecting mothers across all cages • Real-time data from cage rooms</CardDescription>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => { window.dataRefreshTriggered = true; fetchData(); }}
                      >
                        Refresh
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <AnimalTable animals={getGroupAnimals("pregnant")} />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="weaning">
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle>Weaning ({getGroupAnimals("weaning").length})</CardTitle>
                        <CardDescription>Animals being weaned across all cages • Real-time data from cage rooms</CardDescription>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => { window.dataRefreshTriggered = true; fetchData(); }}
                      >
                        Refresh
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <AnimalTable animals={getGroupAnimals("weaning")} />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="sold">
                <div className="space-y-6">
                  {/* Sold Animals Table */}
                  <Card>
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <div>
                          <CardTitle>Sold Animals ({getGroupAnimals("sold").length})</CardTitle>
                          <CardDescription>Animals that have been sold • Real-time data from cage rooms</CardDescription>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => { window.dataRefreshTriggered = true; fetchData(); }}
                        >
                          Refresh
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <AnimalTable animals={getGroupAnimals("sold")} />
                    </CardContent>
                  </Card>

                  {/* Billing Form */}
                  <Card className="border-2 border-blue-200">
                    <CardHeader className="bg-gradient-to-r from-blue-50 to-white">
                      <CardTitle className="text-2xl text-blue-800">Create Billing Form</CardTitle>
                      <CardDescription>Generate a bill for a sold animal</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6">
                      <form onSubmit={async (e) => {
                        e.preventDefault();
                        const formData = new FormData(e.target);
                        const billData = {
                          animal_id: formData.get('animal_id'),
                          animal_breed: formData.get('animal_breed'),
                          animal_type: formData.get('animal_type'),
                          buyer_name: formData.get('buyer_name'),
                          buyer_phone: formData.get('buyer_phone'),
                          buyer_email: formData.get('buyer_email'),
                          buyer_address: formData.get('buyer_address'),
                          quantity: parseInt(formData.get('quantity')),
                          unit_price: parseFloat(formData.get('unit_price')),
                          total_amount: parseFloat(formData.get('unit_price')) * parseInt(formData.get('quantity')),
                          payment_method: formData.get('payment_method'),
                          notes: formData.get('notes')
                        };

                        try {
                          await axios.post(`${API}/bills`, billData, { headers: getAuthHeaders() });
                          toast.success('Bill created successfully');
                          e.target.reset();
                          fetchData();
                          fetchBills();
                        } catch (error) {
                          toast.error('Failed to create bill');
                          console.error(error);
                        }
                      }}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="animal_id">Select Animal *</Label>
                            <Select name="animal_id" required>
                              <SelectTrigger>
                                <SelectValue placeholder="Choose animal..." />
                              </SelectTrigger>
                              <SelectContent>
                                {animals.filter(a => a.group === 'ready_to_sell' || a.group === 'sold').map(animal => (
                                  <SelectItem key={animal.id} value={animal.id}>
                                    {animal.breed} ({animal.animal_type}) - Cage {animal.cage_number}-{animal.sub_room}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label htmlFor="buyer_name">Buyer Name *</Label>
                            <Input name="buyer_name" required placeholder="Enter buyer name" />
                          </div>

                          <div>
                            <Label htmlFor="buyer_phone">Buyer Phone *</Label>
                            <Input name="buyer_phone" required placeholder="Enter phone number" />
                          </div>

                          <div>
                            <Label htmlFor="buyer_email">Buyer Email</Label>
                            <Input name="buyer_email" type="email" placeholder="Enter email (optional)" />
                          </div>

                          <div className="md:col-span-2">
                            <Label htmlFor="buyer_address">Buyer Address</Label>
                            <Input name="buyer_address" placeholder="Enter address (optional)" />
                          </div>

                          <div>
                            <Label htmlFor="quantity">Quantity *</Label>
                            <Input name="quantity" type="number" min="1" defaultValue="1" required />
                          </div>

                          <div>
                            <Label htmlFor="unit_price">Unit Price (₵) *</Label>
                            <Input name="unit_price" type="number" step="0.01" min="0" required placeholder="0.00" />
                          </div>

                          <div>
                            <Label htmlFor="payment_method">Payment Method *</Label>
                            <Select name="payment_method" required>
                              <SelectTrigger>
                                <SelectValue placeholder="Choose payment method..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="cash">Cash</SelectItem>
                                <SelectItem value="mobile_money">Mobile Money</SelectItem>
                                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                                <SelectItem value="cheque">Cheque</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="md:col-span-2">
                            <Label htmlFor="notes">Notes</Label>
                            <Textarea name="notes" placeholder="Additional notes (optional)" rows={3} />
                          </div>

                          <input type="hidden" name="animal_breed" />
                          <input type="hidden" name="animal_type" />
                        </div>

                        <div className="mt-6 flex justify-end gap-2">
                          <Button type="button" variant="outline" onClick={(e) => e.target.closest('form').reset()}>
                            Clear
                          </Button>
                          <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                            Create Bill
                          </Button>
                        </div>
                      </form>
                    </CardContent>
                  </Card>

                  {/* Bills List */}
                  <Card className="border-2 border-green-200">
                    <CardHeader className="bg-gradient-to-r from-green-50 to-white">
                      <CardTitle className="text-2xl text-green-800">Generated Bills ({bills.length})</CardTitle>
                      <CardDescription>All sales invoices and bills</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6">
                      {bills.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <p>No bills created yet. Create a bill using the form above.</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {bills.map(bill => (
                            <Card key={bill.id} className="border-2 border-green-100 bg-white hover:shadow-md transition-all">
                              <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3">
                                      <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                                        <span className="text-green-600 font-bold text-lg">
                                          ₵
                                        </span>
                                      </div>
                                      <div>
                                        <h3 className="font-semibold text-gray-800 text-lg">{bill.bill_number}</h3>
                                        <p className="text-sm text-gray-600">Buyer: {bill.buyer_name}</p>
                                      </div>
                                    </div>
                                    <div className="mt-2 ml-15 text-sm text-gray-600">
                                      <p>
                                        <span className="font-semibold">Animal:</span> {bill.animal_breed} ({bill.animal_type})
                                      </p>
                                      <p>
                                        <span className="font-semibold">Quantity:</span> {bill.quantity} • 
                                        <span className="font-semibold ml-2">Total:</span> ₵{bill.total_amount.toFixed(2)}
                                      </p>
                                      <p>
                                        <span className="font-semibold">Payment:</span> {bill.payment_method.replace('_', ' ').toUpperCase()}
                                      </p>
                                      <p className="text-xs text-gray-500 mt-1">
                                        Created: {new Date(bill.bill_date).toLocaleDateString()} at {new Date(bill.bill_date).toLocaleTimeString()} by {bill.created_by}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex flex-col gap-2">
                                    <Button
                                      size="sm"
                                      onClick={async () => {
                                        try {
                                          const response = await axios.get(`${API}/bills/${bill.id}/pdf`, {
                                            headers: getAuthHeaders(),
                                            responseType: 'blob'
                                          });
                                          const url = window.URL.createObjectURL(new Blob([response.data]));
                                          const link = document.createElement('a');
                                          link.href = url;
                                          link.setAttribute('download', `bill-${bill.bill_number}.pdf`);
                                          document.body.appendChild(link);
                                          link.click();
                                          link.remove();
                                          window.URL.revokeObjectURL(url);
                                          toast.success('Bill downloaded successfully');
                                        } catch (error) {
                                          toast.error('Failed to download bill');
                                          console.error(error);
                                        }
                                      }}
                                      className="bg-blue-600 hover:bg-blue-700"
                                    >
                                      📄 Download PDF
                                    </Button>
                                    {user?.is_admin && (
                                      <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={async () => {
                                          if (!window.confirm('Are you sure you want to delete this bill?')) return;
                                          try {
                                            await axios.delete(`${API}/bills/${bill.id}`, { headers: getAuthHeaders() });
                                            toast.success('Bill deleted');
                                            fetchBills();
                                          } catch (error) {
                                            toast.error('Failed to delete bill');
                                          }
                                        }}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="ready_to_sell">
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle>Ready to Sell ({getGroupAnimals("ready_to_sell").length})</CardTitle>
                        <CardDescription>Market-ready animals across all cages • Real-time data from cage rooms</CardDescription>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => { window.dataRefreshTriggered = true; fetchData(); }}
                      >
                        Refresh
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <AnimalTable animals={getGroupAnimals("ready_to_sell")} />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* CHAT ROOM TAB */}
          <TabsContent value="chat">
            <Card>
              <CardHeader>
                <CardTitle>Farm Chat Room</CardTitle>
                <CardDescription>Team communication</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="chat-container">
                  <div className="chat-messages">
                  {messages.map(msg => (
                    <div key={msg.id} className="chat-message">
                      <div className="message-avatar">
                        {msg.username.charAt(0).toUpperCase()}
                      </div>
                      <div className="message-content">
                        <div className="message-header">
                          <span className="message-username">{msg.username}</span>
                          <span className="message-time">{formatTime(msg.created_at)}</span>
                        </div>
                        {msg.message && <div className="message-text">{msg.message}</div>}
                        {msg.media_data && (
                          <div className="message-media">
                            {msg.media_type === 'photo' ? (
                              <img src={msg.media_data} alt="Shared photo" />
                            ) : (
                              <video src={msg.media_data} controls />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
                <div className="chat-input-container">
                  <form onSubmit={handleSendMessage}>
                    {mediaPreview && (
                      <div className="media-preview">
                        <div className="relative inline-block">
                          {mediaType === 'photo' ? (
                            <img src={mediaPreview} alt="Preview" />
                          ) : (
                            <video src={mediaPreview} controls />
                          )}
                          <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            className="absolute top-2 right-2"
                            onClick={() => {
                              setMediaPreview(null);
                              setMediaType(null);
                            }}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                    <div className="chat-input-wrapper">
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/*,video/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const type = file.type.startsWith('image/') ? 'photo' : 'video';
                            handleFileSelect(e, type);
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Image className="w-4 h-4" />
                      </Button>
                      <Textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message..."
                        className="flex-1 min-h-[40px] max-h-[100px]"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage(e);
                          }
                        }}
                      />
                      <Button type="submit" size="sm">
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </form>
                </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* USER CONTROLS TAB */}
          {user?.is_admin && (
            <TabsContent value="users">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>User Management</CardTitle>
                      <CardDescription>Manage farm users and permissions</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => setShowPasswordReset(true)} variant="outline">
                        Reset My Password
                      </Button>
                      <Button 
                        onClick={() => {
                          fetchPendingChanges();
                          setShowPendingChanges(true);
                        }} 
                        variant="outline"
                        className="relative"
                      >
                        Pending Approvals
                        {pendingChanges.length > 0 && (
                          <Badge className="absolute -top-2 -right-2 bg-red-500 text-white">
                            {pendingChanges.length}
                          </Badge>
                        )}
                      </Button>
                      <Button 
                        onClick={() => {
                          fetchActivityLogs();
                          setShowActivityLogs(true);
                        }} 
                        variant="outline"
                      >
                        View Activity Log
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="user-management">
                  <form onSubmit={handleAddUser} className="space-y-4 mb-6 p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold">Add New User</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="newUsername">Username *</Label>
                        <Input
                          id="newUsername"
                          value={newUsername}
                          onChange={(e) => setNewUsername(e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="newPassword">Password *</Label>
                        <Input
                          id="newPassword"
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="newUserFullName">Full Name</Label>
                        <Input
                          id="newUserFullName"
                          value={newUserFullName}
                          onChange={(e) => setNewUserFullName(e.target.value)}
                          placeholder="John Doe"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="newUserEmail">Email</Label>
                        <Input
                          id="newUserEmail"
                          type="email"
                          value={newUserEmail}
                          onChange={(e) => setNewUserEmail(e.target.value)}
                          placeholder="john@example.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="newUserPhone">Phone</Label>
                        <Input
                          id="newUserPhone"
                          value={newUserPhone}
                          onChange={(e) => setNewUserPhone(e.target.value)}
                          placeholder="+1234567890"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="newUserAddress">Address</Label>
                        <Input
                          id="newUserAddress"
                          value={newUserAddress}
                          onChange={(e) => setNewUserAddress(e.target.value)}
                          placeholder="123 Main St, City"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="newUserNextOfKin">Next of Kin Name</Label>
                        <Input
                          id="newUserNextOfKin"
                          value={newUserNextOfKin}
                          onChange={(e) => setNewUserNextOfKin(e.target.value)}
                          placeholder="Jane Doe"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="newUserNextOfKinContact">Next of Kin Contact</Label>
                        <Input
                          id="newUserNextOfKinContact"
                          value={newUserNextOfKinContact}
                          onChange={(e) => setNewUserNextOfKinContact(e.target.value)}
                          placeholder="+1234567890"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="isAdmin"
                        checked={isAdmin}
                        onChange={(e) => setIsAdmin(e.target.checked)}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="isAdmin">Admin User</Label>
                    </div>
                    <Button type="submit">Add User</Button>
                  </form>

                  <div className="user-list">
                    {users.map(u => (
                      <div key={u.id} className="user-item">
                        <div className="user-info">
                          <div className="user-avatar">
                            {u.username.charAt(0).toUpperCase()}
                          </div>
                          <div className="user-details">
                            <div className="user-name">{u.username}</div>
                            <div className="user-role">{u.is_admin ? 'Administrator' : 'User'}</div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditUserProfile(u)}
                          >
                            Edit Profile
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              try {
                                const response = await axios.get(`${API}/users/${u.id}/password`, { headers: getAuthHeaders() });
                                alert(`Username: ${response.data.username}\nPassword: ${response.data.password}`);
                              } catch (error) {
                                toast.error("Failed to retrieve password");
                              }
                            }}
                          >
                            View Password
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setResetTargetUsername(u.username);
                              setShowAdminPasswordReset(true);
                            }}
                          >
                            Reset Password
                          </Button>
                          {u.id !== user?.id && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteUser(u.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Animal Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAnimal ? "Edit Animal" : "Add New Animal"}</DialogTitle>
            <DialogDescription>
              {editingAnimal ? "Update the animal details" : "Fill in the details for the new animal"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="animal_type">Animal Type</Label>
                <Select 
                  value={formData.animal_type} 
                  onValueChange={(value) => setFormData({...formData, animal_type: value})}
                >
                  <SelectTrigger id="animal_type" data-testid="animal-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rabbit">Rabbit</SelectItem>
                    <SelectItem value="grass_cutter">Grass Cutter</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="breed">Breed</Label>
                <Input
                  id="breed"
                  value={formData.breed}
                  onChange={(e) => setFormData({...formData, breed: e.target.value})}
                  required
                  data-testid="breed-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="age">Age (months)</Label>
                <Input
                  id="age"
                  type="number"
                  value={formData.age}
                  onChange={(e) => setFormData({...formData, age: e.target.value})}
                  required
                  data-testid="age-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="gender">Gender</Label>
                <Select 
                  value={formData.gender} 
                  onValueChange={(value) => setFormData({...formData, gender: value})}
                >
                  <SelectTrigger id="gender" data-testid="gender-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="weight">Weight (kg)</Label>
                <Input
                  id="weight"
                  type="number"
                  step="0.1"
                  value={formData.weight}
                  onChange={(e) => setFormData({...formData, weight: e.target.value})}
                  required
                  data-testid="weight-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="purchase_date">Purchase Date</Label>
                <Input
                  id="purchase_date"
                  type="date"
                  value={formData.purchase_date}
                  onChange={(e) => setFormData({...formData, purchase_date: e.target.value})}
                  data-testid="purchase-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="birth_date">Birth Date</Label>
                <Input
                  id="birth_date"
                  type="date"
                  value={formData.birth_date}
                  onChange={(e) => setFormData({...formData, birth_date: e.target.value})}
                  data-testid="birth-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="health_status">Health Status</Label>
                <Select 
                  value={formData.health_status} 
                  onValueChange={(value) => setFormData({...formData, health_status: value})}
                >
                  <SelectTrigger id="health_status" data-testid="health-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="healthy">Healthy</SelectItem>
                    <SelectItem value="sick">Sick</SelectItem>
                    <SelectItem value="under_treatment">Under Treatment</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cage_number">Cage Number</Label>
                <Input
                  id="cage_number"
                  value={formData.cage_number}
                  onChange={(e) => setFormData({...formData, cage_number: e.target.value})}
                  required
                  data-testid="cage-number-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sub_room">Sub-Room</Label>
                <Select 
                  value={formData.sub_room} 
                  onValueChange={(value) => setFormData({...formData, sub_room: value})}
                >
                  <SelectTrigger id="sub_room">
                    <SelectValue placeholder="Select sub-room" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A">Room A</SelectItem>
                    <SelectItem value="B">Room B</SelectItem>
                    <SelectItem value="C">Room C</SelectItem>
                    <SelectItem value="D">Room D</SelectItem>
                    <SelectItem value="E">Room E</SelectItem>
                    <SelectItem value="F">Room F</SelectItem>
                    <SelectItem value="G">Room G</SelectItem>
                    <SelectItem value="H">Room H</SelectItem>
                    <SelectItem value="I">Room I</SelectItem>
                    <SelectItem value="J">Room J</SelectItem>
                    <SelectItem value="K">Room K</SelectItem>
                    <SelectItem value="L">Room L</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 col-span-2">
                <Label htmlFor="photo">Animal Photo</Label>
                <input
                  type="file"
                  ref={animalPhotoRef}
                  accept="image/*"
                  className="hidden"
                  onChange={handleAnimalPhotoSelect}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => animalPhotoRef.current?.click()}
                  className="w-full"
                >
                  <Image className="w-4 h-4 mr-2" />
                  {formData.photo ? "Change Photo" : "Upload Photo"}
                </Button>
                {formData.photo && (
                  <div className="animal-photo-container mt-2">
                    <img
                      src={formData.photo}
                      alt="Animal preview"
                      className="animal-photo-preview"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      className="animal-photo-remove"
                      onClick={() => setFormData({...formData, photo: ""})}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-2 col-span-2">
                <Label htmlFor="group">Group</Label>
                <Select 
                  value={formData.group} 
                  onValueChange={(value) => setFormData({...formData, group: value})}
                >
                  <SelectTrigger id="group" data-testid="group-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="babies">Babies</SelectItem>
                    <SelectItem value="mothers">Mothers</SelectItem>
                    <SelectItem value="crossed">Crossed</SelectItem>
                    <SelectItem value="pregnant">Pregnant</SelectItem>
                    <SelectItem value="weaning">Weaning</SelectItem>
                    <SelectItem value="sold">Sold</SelectItem>
                    <SelectItem value="ready_to_sell">Ready to Sell</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.group === "pregnant" && (
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="pregnancy_start_date">Pregnancy Start Date</Label>
                  <Input
                    id="pregnancy_start_date"
                    type="date"
                    value={formData.pregnancy_start_date}
                    onChange={(e) => setFormData({...formData, pregnancy_start_date: e.target.value})}
                  />
                  <p className="text-xs text-gray-500">
                    Leave empty to use today's date. Gestation: {formData.animal_type === 'rabbit' ? '31 days' : '180 days (6 months)'}
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-animal-button">
                {editingAnimal ? "Update" : "Add"} Animal
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Sub-Rooms Dialog */}
      <Dialog open={subRoomDialogOpen} onOpenChange={setSubRoomDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          {selectedCage && (
            <CageDetails
              cageNumber={selectedCage}
              animals={animals}
              getAuthHeaders={getAuthHeaders}
              onClose={() => setSubRoomDialogOpen(false)}
              onRefresh={fetchData}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={showPasswordReset} onOpenChange={setShowPasswordReset}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>Change your account password</DialogDescription>
          </DialogHeader>
          <form onSubmit={handlePasswordReset} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current_password">Current Password</Label>
              <Input
                id="current_password"
                type="password"
                value={currentPasswordInput}
                onChange={(e) => setCurrentPasswordInput(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                type="password"
                value={newPasswordReset}
                onChange={(e) => setNewPasswordReset(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <Input
                id="confirm_password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowPasswordReset(false)}>
                Cancel
              </Button>
              <Button type="submit">Reset Password</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Admin Password Reset Dialog */}
      <Dialog open={showAdminPasswordReset} onOpenChange={setShowAdminPasswordReset}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset User Password</DialogTitle>
            <DialogDescription>Set a new password for {resetTargetUsername}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAdminPasswordReset} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="target_username">Username</Label>
              <Input
                id="target_username"
                type="text"
                value={resetTargetUsername}
                readOnly
                className="bg-gray-50"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin_new_password">New Password</Label>
              <Input
                id="admin_new_password"
                type="password"
                value={newPasswordReset}
                onChange={(e) => setNewPasswordReset(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowAdminPasswordReset(false);
                  setResetTargetUsername("");
                  setNewPasswordReset("");
                }}
              >
                Cancel
              </Button>
              <Button type="submit">Reset Password</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Activity Log Dialog */}
      <Dialog open={showActivityLogs} onOpenChange={setShowActivityLogs}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Activity Log</DialogTitle>
            <DialogDescription>Recent system activities and user actions</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {activityLogs.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No activities recorded</p>
            ) : (
              activityLogs.map((log) => (
                <div key={log.id} className="p-3 border rounded-lg bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">{log.username}</span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          log.action === 'create' ? 'bg-green-100 text-green-700' :
                          log.action === 'update' ? 'bg-blue-100 text-blue-700' :
                          log.action === 'delete' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {log.action}
                        </span>
                        <span className="text-xs text-gray-500">{log.entity_type}</span>
                      </div>
                      <p className="text-sm mt-1">{log.details}</p>
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap ml-2">
                      {new Date(log.timestamp).toLocaleString()}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setShowActivityLogs(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pending Changes Dialog */}
      <Dialog open={showPendingChanges} onOpenChange={setShowPendingChanges}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pending Approvals ({pendingChanges.length})</DialogTitle>
            <DialogDescription>Review and approve/reject changes submitted by users</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {pendingChanges.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No pending changes</p>
            ) : (
              pendingChanges.map((change) => (
                <div key={change.id} className="p-4 border-2 rounded-lg bg-yellow-50 border-yellow-200">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{change.username}</span>
                        <Badge className={`${
                          change.action === 'create' ? 'bg-green-500' :
                          change.action === 'update' ? 'bg-blue-500' :
                          'bg-red-500'
                        } text-white`}>
                          {change.action}
                        </Badge>
                        <span className="text-sm text-gray-600">{change.entity_type}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(change.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  
                  {/* Show change details */}
                  <div className="mt-3 p-3 bg-white rounded border">
                    {change.action === 'create' && (
                      <div>
                        <p className="font-semibold text-sm mb-2">New Animal:</p>
                        <p className="text-sm">Type: {change.entity_data?.animal_type?.replace('_', ' ')}</p>
                        <p className="text-sm">Breed: {change.entity_data?.breed}</p>
                        <p className="text-sm">Age: {change.entity_data?.age} months</p>
                        <p className="text-sm">Group: {change.entity_data?.group?.replace('_', ' ')}</p>
                        <p className="text-sm">Cage: {change.entity_data?.cage_number || 'N/A'}</p>
                      </div>
                    )}
                    {change.action === 'update' && (
                      <div>
                        <p className="font-semibold text-sm mb-2">Changes to {change.original_data?.breed}:</p>
                        {Object.keys(change.entity_data).map(key => (
                          <p key={key} className="text-sm">
                            {key}: <span className="text-red-500 line-through">{change.original_data?.[key]}</span> → 
                            <span className="text-green-500 font-semibold"> {change.entity_data[key]}</span>
                          </p>
                        ))}
                      </div>
                    )}
                    {change.action === 'delete' && (
                      <div>
                        <p className="font-semibold text-sm mb-2">Delete Request:</p>
                        <p className="text-sm">Type: {change.original_data?.animal_type?.replace('_', ' ')}</p>
                        <p className="text-sm">Breed: {change.original_data?.breed}</p>
                        <p className="text-sm">Group: {change.original_data?.group?.replace('_', ' ')}</p>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 mt-3">
                    <Button 
                      onClick={() => handleApprovePendingChange(change.id)}
                      className="bg-green-500 hover:bg-green-600"
                      size="sm"
                    >
                      Approve
                    </Button>
                    <Button 
                      onClick={() => handleRejectPendingChange(change.id)}
                      variant="destructive"
                      size="sm"
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setShowPendingChanges(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* My Profile Dialog */}
      <Dialog open={showMyProfile} onOpenChange={setShowMyProfile}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>My Profile</DialogTitle>
            <DialogDescription>View and edit your profile information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            {/* Profile Photo */}
            <div className="flex flex-col items-center space-y-3">
              {profilePhoto ? (
                <img src={profilePhoto} alt="Profile" className="w-32 h-32 rounded-full object-cover border-4 border-gray-200" />
              ) : (
                <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center text-4xl font-bold text-gray-500">
                  {user?.username?.charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <Label htmlFor="profile_photo" className="cursor-pointer">
                  <div className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-center">
                    {profilePhoto ? 'Change Photo' : 'Upload Photo'}
                  </div>
                </Label>
                <input
                  id="profile_photo"
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePhotoChange}
                  className="hidden"
                />
              </div>
            </div>

            {/* Read-only fields */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded">
              <div className="space-y-2">
                <Label className="text-gray-500">Username (Read-only)</Label>
                <Input value={user?.username || ""} readOnly className="bg-gray-100" />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-500">Full Name (Read-only)</Label>
                <Input value={user?.full_name || ""} readOnly className="bg-gray-100" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label className="text-gray-500">Address (Read-only)</Label>
                <Input value={user?.address || ""} readOnly className="bg-gray-100" />
              </div>
            </div>

            {/* Editable fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="profile_email">Email *</Label>
                <Input
                  id="profile_email"
                  type="email"
                  value={profileEmail}
                  onChange={(e) => setProfileEmail(e.target.value)}
                  placeholder="your@email.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="profile_phone">Phone / Contact *</Label>
                <Input
                  id="profile_phone"
                  value={profilePhone}
                  onChange={(e) => setProfilePhone(e.target.value)}
                  placeholder="+1234567890"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="profile_next_of_kin">Next of Kin Name *</Label>
                <Input
                  id="profile_next_of_kin"
                  value={profileNextOfKin}
                  onChange={(e) => setProfileNextOfKin(e.target.value)}
                  placeholder="John Doe"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="profile_next_of_kin_contact">Next of Kin Contact *</Label>
                <Input
                  id="profile_next_of_kin_contact"
                  value={profileNextOfKinContact}
                  onChange={(e) => setProfileNextOfKinContact(e.target.value)}
                  placeholder="+1234567890"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowMyProfile(false)}>
                Cancel
              </Button>
              <Button type="submit">Save Changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Admin Edit User Profile Dialog */}
      <Dialog open={showEditUserProfile} onOpenChange={setShowEditUserProfile}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit User Profile - {editingUser?.username}</DialogTitle>
            <DialogDescription>Admin: Edit all profile fields for this user</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAdminUpdateProfile} className="space-y-4">
            {/* Profile Photo */}
            <div className="flex flex-col items-center space-y-3">
              {editUserProfilePhoto ? (
                <img src={editUserProfilePhoto} alt="Profile" className="w-32 h-32 rounded-full object-cover border-4 border-gray-200" />
              ) : (
                <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center text-4xl font-bold text-gray-500">
                  {editingUser?.username?.charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <Label htmlFor="edit_user_photo" className="cursor-pointer">
                  <div className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-center">
                    {editUserProfilePhoto ? 'Change Photo' : 'Upload Photo'}
                  </div>
                </Label>
                <input
                  id="edit_user_photo"
                  type="file"
                  accept="image/*"
                  onChange={handleEditUserPhotoChange}
                  className="hidden"
                />
              </div>
            </div>

            {/* Read-only username */}
            <div className="p-3 bg-gray-50 rounded">
              <Label className="text-gray-500">Username (Read-only)</Label>
              <Input value={editingUser?.username || ""} readOnly className="bg-gray-100 mt-1" />
            </div>

            {/* All editable fields for admin */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_full_name">Full Name</Label>
                <Input
                  id="edit_full_name"
                  value={editUserFullName}
                  onChange={(e) => setEditUserFullName(e.target.value)}
                  placeholder="John Doe"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_email">Email</Label>
                <Input
                  id="edit_email"
                  type="email"
                  value={editUserEmail}
                  onChange={(e) => setEditUserEmail(e.target.value)}
                  placeholder="user@email.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_phone">Phone / Contact</Label>
                <Input
                  id="edit_phone"
                  value={editUserPhone}
                  onChange={(e) => setEditUserPhone(e.target.value)}
                  placeholder="+1234567890"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label htmlFor="edit_address">Address</Label>
                <Input
                  id="edit_address"
                  value={editUserAddress}
                  onChange={(e) => setEditUserAddress(e.target.value)}
                  placeholder="123 Main St, City"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_next_of_kin">Next of Kin Name</Label>
                <Input
                  id="edit_next_of_kin"
                  value={editUserNextOfKin}
                  onChange={(e) => setEditUserNextOfKin(e.target.value)}
                  placeholder="Jane Doe"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_next_of_kin_contact">Next of Kin Contact</Label>
                <Input
                  id="edit_next_of_kin_contact"
                  value={editUserNextOfKinContact}
                  onChange={(e) => setEditUserNextOfKinContact(e.target.value)}
                  placeholder="+1234567890"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowEditUserProfile(false)}>
                Cancel
              </Button>
              <Button type="submit">Save Changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Task Creation Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Task</DialogTitle>
            <DialogDescription>Create a task or announcement for all users</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="taskTitle">Task Title *</Label>
              <Input
                id="taskTitle"
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                placeholder="Enter task title..."
              />
            </div>
            <div>
              <Label htmlFor="taskDescription">Description (Optional)</Label>
              <Textarea
                id="taskDescription"
                value={newTaskDescription}
                onChange={(e) => setNewTaskDescription(e.target.value)}
                placeholder="Add more details..."
                rows={4}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setShowTaskDialog(false);
                setNewTaskTitle("");
                setNewTaskDescription("");
              }}>
                Cancel
              </Button>
              <Button onClick={handleCreateTask} className="bg-green-600 hover:bg-green-700">
                Create Task
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Sub-Room Dialog */}
      <Dialog open={showAddSubRoomDialog} onOpenChange={setShowAddSubRoomDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Sub-Room</DialogTitle>
            <DialogDescription>Add a sub-room to an existing cage</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="addSubRoomCage">Select Cage *</Label>
              <Select value={addSubRoomCage} onValueChange={setAddSubRoomCage}>
                <SelectTrigger id="addSubRoomCage">
                  <SelectValue placeholder="Choose cage..." />
                </SelectTrigger>
                <SelectContent>
                  {allCages.map(cage => (
                    <SelectItem key={cage} value={cage}>{cage}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="newSubRoomLabel">Sub-Room Label *</Label>
              <Input
                id="newSubRoomLabel"
                value={newSubRoomLabel}
                onChange={(e) => setNewSubRoomLabel(e.target.value)}
                placeholder="e.g., M, N, O or 13, 14..."
                maxLength={3}
              />
              <p className="text-xs text-gray-500 mt-1">
                Use letters (M, N, O...) or numbers (13, 14...). Will be converted to uppercase.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setShowAddSubRoomDialog(false);
                setNewSubRoomLabel("");
                setAddSubRoomCage("");
              }}>
                Cancel
              </Button>
              <Button onClick={handleAddSubRoom} className="bg-blue-600 hover:bg-blue-700">
                Add Sub-Room
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return user ? <MainApp /> : <Login />;
}

function AppWrapper() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}

export default AppWrapper;
